import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";

export default function ConnectCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="connect"
      data-testid="connect-cta-section"
      className="pt-[25px] pb-[55px] md:pt-[75px] md:pb-[55px]"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.96 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[2rem] overflow-hidden py-12 md:py-16 px-8 md:px-16 text-center border border-black/[0.04]"
          style={{ background: 'linear-gradient(180deg, #f8faff 0%, #f0f4ff 50%, #f5f7fc 100%)', boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
        >
          {/* Subtle grid */}
          <div className="absolute inset-0 grid-pattern opacity-10" />

          {/* Vignette */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(15,23,42,0.025) 100%)' }}
          />

          {/* Left circle — large, shifted higher, blue tone, 12-18% opacity */}
          <div className="absolute pointer-events-none hidden md:block" style={{ top: '15%', left: '-12%' }}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[420px] md:h-[420px] rounded-full blur-[90px]"
              style={{ background: 'radial-gradient(circle, rgba(79,107,255,0.12) 0%, transparent 65%)' }}
            />
            <svg width="480" height="480" viewBox="0 0 480 480" fill="none" className="w-[300px] h-[300px] md:w-[480px] md:h-[480px]">
              <circle cx="240" cy="240" r="230" stroke="rgba(79,107,255,0.16)" strokeWidth="1" />
              <circle cx="240" cy="240" r="165" stroke="rgba(79,107,255,0.11)" strokeWidth="1" />
              <circle cx="240" cy="240" r="90" stroke="rgba(79,107,255,0.07)" strokeWidth="1" />
            </svg>
          </div>

          {/* Right circle — smaller, shifted lower, violet tint #7B61FF */}
          <motion.div
            className="absolute pointer-events-none"
            style={{ bottom: '2%', right: '-8%' }}
            animate={{ x: [0, -12, 5, 0], y: [0, -10, 8, 0], scale: [1, 1.05, 0.97, 1] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[220px] h-[220px] md:w-[310px] md:h-[310px] rounded-full blur-[80px]"
              style={{ background: 'radial-gradient(circle, rgba(123,97,255,0.18) 0%, transparent 65%)' }}
            />
            <svg width="360" height="360" viewBox="0 0 360 360" fill="none" className="w-[225px] h-[225px] md:w-[360px] md:h-[360px]">
              <circle cx="180" cy="180" r="172" stroke="rgba(123,97,255,0.22)" strokeWidth="1.2" />
              <circle cx="180" cy="180" r="120" stroke="rgba(123,97,255,0.16)" strokeWidth="1" />
              <circle cx="180" cy="180" r="60" stroke="rgba(123,97,255,0.10)" strokeWidth="1" />
              <circle r="4" fill="rgba(123,97,255,0.55)">
                <animateMotion dur="18s" repeatCount="indefinite" path="M 180 8 A 172 172 0 0 1 352 180 A 172 172 0 0 1 180 352 A 172 172 0 0 1 8 180 A 172 172 0 0 1 180 8" />
                <animate attributeName="opacity" values="0.55;0.25;0.55" dur="3s" repeatCount="indefinite" />
              </circle>
            </svg>
          </motion.div>

          {/* Central glow behind CTA — main focal point */}
          <div className="absolute bottom-[14%] left-1/2 -translate-x-1/2 pointer-events-none">
            <div className="w-[400px] h-[400px] md:w-[550px] md:h-[550px] rounded-full blur-[120px]"
              style={{ background: 'radial-gradient(circle at center, rgba(79,107,255,0.22) 0%, rgba(123,97,255,0.12) 35%, transparent 70%)' }}
            />
          </div>

          <div className="relative z-10">
            <motion.h2
              data-testid="connect-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-[21px]"
            >
              Connect with us
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="font-body text-base md:text-lg leading-relaxed text-[#475569] max-w-xl mx-auto mb-8"
            >
              We welcome investment inquiries from companies building in regulated and infrastructure-led markets.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <a
                href="/contact"
                data-testid="connect-cta-button"
                className="connect-cta-btn group inline-flex items-center gap-2 px-[18px] py-[10px] text-white text-[14px] font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5"
              >
                Get in touch
                <ArrowUpRight className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
